import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Copy, ExternalLink, Code } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

/**
 * Schema Validation Test Page
 * Displays all schema types for easy testing with Google Rich Results Test
 */
const SchemaValidationTest = () => {
  const [faqs, setFaqs] = useState([]);
  const [copied, setCopied] = useState('');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/faqs`);
      setFaqs(response.data.faqs.slice(0, 5)); // First 5 FAQs for testing
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const schemas = {
    breadcrumb: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.ascarii.com/" },
        { "@type": "ListItem", "position": 2, "name": "SAP B1 Extensions", "item": "https://www.ascarii.com/#products" },
        { "@type": "ListItem", "position": 3, "name": "Beas Manufacturing" }
      ]
    },
    product: {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Beas Manufacturing for SAP Business One",
      "description": "Comprehensive manufacturing solution with production planning, shop floor control, and quality management for SAP Business One.",
      "brand": { "@type": "Brand", "name": "Beas" },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "900"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "priceCurrency": "GBP",
        "seller": { "@type": "Organization", "name": "Ascarii" }
      }
    },
    faq: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
      }))
    },
    article: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "How Cloud-Based SAP Business One Helps CIOs Overcome Top Challenges",
      "description": "Discover how cloud-based SAP Business One addresses key challenges for CIOs and IT Directors.",
      "author": { "@type": "Person", "name": "Ascarii Team" },
      "publisher": {
        "@type": "Organization",
        "name": "Ascarii",
        "logo": { "@type": "ImageObject", "url": "https://www.ascarii.com/ascarii-logo.png" }
      },
      "datePublished": "2025-10-13",
      "dateModified": "2025-10-13"
    },
    organization: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Ascarii",
      "url": "https://www.ascarii.com",
      "logo": "https://www.ascarii.com/ascarii-logo.png",
      "description": "SAP Premier Partner and AWS Advanced Partner specializing in SAP Business One for UK SMEs",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "NETPark, Thomas Wright Way",
        "addressLocality": "Sedgefield",
        "addressRegion": "County Durham",
        "postalCode": "TS21 3FD",
        "addressCountry": "GB"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+44-1789-777-466",
        "contactType": "customer service",
        "email": "info@ascarii.com"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "150"
      }
    }
  };

  const copySchema = (schemaName) => {
    const schema = schemas[schemaName];
    navigator.clipboard.writeText(JSON.stringify(schema, null, 2));
    setCopied(schemaName);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Schema Validation Test Page</h1>
          <p className="text-lg text-gray-600 mb-6">Test your structured data with Google's Rich Results Test</p>
          <a 
            href="https://search.google.com/test/rich-results" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          >
            Open Google Rich Results Test
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {Object.entries(schemas).map(([name, schema]) => (
            <motion.div
              key={name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900 capitalize">{name} Schema</h3>
                <Button
                  onClick={() => copySchema(name)}
                  variant="outline"
                  className="gap-2"
                  size="sm"
                >
                  {copied === name ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </Button>
              </div>

              <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-green-400 font-mono">
                  {JSON.stringify(schema, null, 2)}
                </pre>
              </div>

              <div className="mt-4 text-sm text-gray-600">
                <p className="font-medium mb-2">Test URLs:</p>
                <ul className="space-y-1 text-xs">
                  {name === 'breadcrumb' && <li>• Any product or landing page</li>}
                  {name === 'product' && <li>• /products/beas-manufacturing</li>}
                  {name === 'faq' && <li>• /faqs</li>}
                  {name === 'article' && <li>• /blog/[any-post]</li>}
                  {name === 'organization' && <li>• / (homepage)</li>}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 bg-blue-50 rounded-2xl p-8 border border-blue-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Test</h2>
          <ol className="space-y-3 text-gray-700">
            <li className="flex gap-3">
              <span className="font-bold text-blue-600">1.</span>
              <span>Copy a schema using the buttons above</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600">2.</span>
              <span>Open <a href="https://search.google.com/test/rich-results" target="_blank" className="text-blue-600 hover:underline">Google Rich Results Test</a></span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600">3.</span>
              <span>Choose "CODE" tab and paste the schema</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600">4.</span>
              <span>Click "Test Code" to validate</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600">5.</span>
              <span>Fix any errors shown and update the component</span>
            </li>
          </ol>
        </div>

        <div className="mt-8 bg-green-50 rounded-2xl p-8 border border-green-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Expected Rich Results</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">✅ Breadcrumbs</h3>
              <p className="text-sm text-gray-600">Navigation path shown in search results</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">⭐ Product Ratings</h3>
              <p className="text-sm text-gray-600">Star ratings displayed in search</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">❓ FAQ Boxes</h3>
              <p className="text-sm text-gray-600">Expandable answers directly in Google</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">📰 Article Cards</h3>
              <p className="text-sm text-gray-600">Enhanced blog post display with images</p>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden schemas for search engines */}
      <script type="application/ld+json">
        {JSON.stringify(schemas.breadcrumb)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemas.product)}
      </script>
      {faqs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(schemas.faq)}
        </script>
      )}
      <script type="application/ld+json">
        {JSON.stringify(schemas.article)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemas.organization)}
      </script>
    </div>
  );
};

export default SchemaValidationTest;
